import { useState, useEffect } from 'react';
import { Book } from '../../types';
import { useStore } from '../../store/useStore';
import { FiMoreVertical, FiTrash2, FiBook, FiStar } from 'react-icons/fi';
import { deleteEPUBFile, deletePDFFile, deleteCoverImage, getCoverImage } from '../../utils/fileStorage';

interface BookCardProps {
  book: Book;
}

const BookCard = ({ book }: BookCardProps) => {
  const { setCurrentBook, setCurrentView, removeBook, updateBook } = useStore();
  const [showMenu, setShowMenu] = useState(false);
  const [coverDataUrl, setCoverDataUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadCover = async () => {
      if (book.cover && book.cover.startsWith('indexed-db-cover://')) {
        const bookId = book.cover.replace('indexed-db-cover://', '');
        const dataUrl = await getCoverImage(bookId);
        if (dataUrl) {
          setCoverDataUrl(dataUrl);
          return;
        }
      } else if (book.cover) {
        // URL 표지 — 실제 존재하는지 확인
        try {
          const res = await fetch(book.cover, { method: 'HEAD' });
          if (res.ok) {
            setCoverDataUrl(book.cover);
            return;
          }
        } catch { /* 네트워크 오류 */ }
      }
      // ✅ 표지가 없거나 깨진 경우 — EPUB에서 직접 추출 시도
      if (book.fileType === 'epub') {
        try {
          const { getEPUBFile, saveCoverImage } = await import('../../utils/fileStorage');
          const ePub = (await import('epubjs')).default;
          const blob = await getEPUBFile(book.filePath);
          if (blob) {
            const ab = await blob.arrayBuffer();
            const epubBook = ePub(ab);
            await epubBook.ready;
            const coverUrl = await epubBook.coverUrl();
            if (coverUrl) {
              const coverRes = await fetch(coverUrl);
              const coverBlob = await coverRes.blob();
              await saveCoverImage(book.id, coverBlob);
              const reader = new FileReader();
              reader.onloadend = () => {
                setCoverDataUrl(reader.result as string);
                updateBook(book.id, { cover: `indexed-db-cover://${book.id}` });
              };
              reader.readAsDataURL(coverBlob);
            }
            epubBook.destroy();
          }
        } catch (e) {
          console.warn('EPUB 표지 자동 추출 실패:', e);
        }
      }
    };
    loadCover();
  }, [book.cover]);

  const handleOpenBook = () => {
    setCurrentBook(book);
    setCurrentView('reader');
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`"${book.title}"을(를) 삭제하시겠습니까?`)) {
      if (book.fileType === 'pdf') {
        await deletePDFFile(book.id);
      } else {
        await deleteEPUBFile(book.id);
      }
      await deleteCoverImage(book.id);
      removeBook(book.id);
    }
    setShowMenu(false);
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateBook(book.id, { favorite: !book.favorite });
    setShowMenu(false);
  };

  const getStatusBadge = () => {
    if (book.status === 'completed') {
      return (
        <span className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full font-medium shadow-md">
          완독
        </span>
      );
    }
    if (book.status === 'reading' && book.progress > 0) {
      return (
        <span className="absolute top-2 right-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full font-medium shadow-md">
          읽는 중
        </span>
      );
    }
    return null;
  };

  return (
    <div
      onClick={handleOpenBook}
      className="book-card group cursor-pointer relative max-w-[160px] mx-auto"
    >
      {/* Cover */}
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden shadow-book group-hover:shadow-book-hover transition-all mb-3 w-full border border-gray-200 dark:border-gray-700">
        {coverDataUrl ? (
          <img
            src={coverDataUrl}
            alt={book.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full gradient-primary flex items-center justify-center">
            <FiBook size={48} className="text-white opacity-50" />
          </div>
        )}

        {/* Status Badge */}
        {getStatusBadge()}

        {/* ✅ 타사 스타일 진행률 바 - 하단 고정 */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-8 pb-2 px-2">
          {/* 진행률 숫자 */}
          <div className="flex items-center justify-center mb-1.5">
            <span className="text-white text-sm font-bold drop-shadow-md">
              {book.progress}%
            </span>
          </div>
          {/* 진행률 바 */}
          <div className="h-2 bg-white/30 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                book.progress >= 100 
                  ? 'bg-green-400' 
                  : book.progress >= 50 
                  ? 'bg-blue-400' 
                  : 'bg-white'
              }`}
              style={{ width: `${book.progress}%` }}
            />
          </div>
        </div>

        {/* Menu Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="absolute top-2 left-2 p-1.5 bg-white/90 dark:bg-gray-800/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
        >
          <FiMoreVertical size={16} />
        </button>

        {/* Dropdown Menu */}
        {showMenu && (
          <div className="absolute top-10 left-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-10 min-w-[140px] border border-gray-200 dark:border-gray-700">
            <button
              onClick={handleToggleFavorite}
              className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FiStar size={16} className={book.favorite ? 'fill-yellow-400 text-yellow-400' : ''} />
              <span>{book.favorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}</span>
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FiTrash2 size={16} />
              <span>삭제</span>
            </button>
          </div>
        )}

        {/* ⭐ Favorite Star Badge */}
        {book.favorite && !showMenu && (
          <div className="absolute top-2 left-2 p-1.5 bg-yellow-400 rounded-full shadow-md">
            <FiStar className="fill-white text-white" size={14} />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-1">
        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1 text-sm leading-tight">
          {book.title}
        </h3>
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
          {book.author}
        </p>
      </div>
    </div>
  );
};

export default BookCard;
